import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { getJournalEntryUnified } from '@/api/transactions';
import type { JournalEntryOut } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/utils';

// ── Journal entry panel ──────────────────────────────────────────────

/** Collapsible panel showing the raw GL lines for the finding's transaction.
 *
 *  Lazy-fetches: the query is only enabled once the user opens the
 *  details element. Cuts ~1 round-trip per finding card on the
 *  default-closed path (the reviewer rarely needs the full journal entry
 *  on every card — just on the surprising ones).
 *
 *  404 maps to a quiet placeholder rather than an error — NetSuite is
 *  the only source today that persists journal entries; QBO-source runs
 *  simply have no rows to show.
 */
export function JournalEntryPanel({
  runId,
  transactionId,
  transactionIdUuid,
}: {
  runId: string;
  transactionId: string;
  /** UUID FK to the `transactions` table. Drives the
   *  `/api/transactions/{id}/journal-entry` fetch; `null` (historical
   *  findings never backfilled) renders the empty state without a
   *  request. */
  transactionIdUuid: string | null;
}) {
  const { userKey, getIdToken } = useAuth();

  const query = useQuery({
    queryKey: ['journal-entry', runId, transactionId, transactionIdUuid, userKey],
    queryFn: async () =>
      getJournalEntryUnified({ runId, transactionIdUuid }, (await getIdToken()) ?? ''),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // No retry on 404 — we use it as the "no journal entry exists"
      // signal for QBO-source runs.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });

  return (
    // `min-w-0` is load-bearing: without it the parent grid track in
    // CardContent expands to the table's natural width (up to ~2000px
    // on a 22-column NetSuite line set), which blows the Card open
    // past the viewport. With `min-w-0` the track stays sized to the
    // available column width and the inner `overflow-x-auto` actually
    // produces a horizontal scrollbar instead of pushing the page wide.
    <section className="min-w-0">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-slate">
        Journal entry
      </h4>
      <JournalEntryPanelBody query={query} />
    </section>
  );
}

// Columns we always promote to the left edge when present, in this order —
// the highest-signal fields for a reviewer auditing how an amount was
// posted. Anything else on the source row drifts to the right in original
// source order (Object.keys insertion).
const _JOURNAL_PRIORITY_COLUMNS = [
  'Account',
  'Account Type',
  'Amount',
  'Date',
  'Type',
  'Document Number',
  'Memo',
  'Description',
] as const;

function JournalEntryPanelBody({
  query,
}: {
  query: ReturnType<typeof useQuery<JournalEntryOut | null>>;
}) {
  if (query.isPending) {
    return <p className="text-sm italic text-brand-slate">Loading journal entry…</p>;
  }
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <p className="text-sm text-brand-slate">
          No journal entry on file. NetSuite-source runs persist the
          per-line GL detail; this run's source didn't.
        </p>
      );
    }
    return (
      <p className="text-sm text-destructive">
        Couldn't load journal entry. Details in the browser console.
      </p>
    );
  }
  const entry = query.data;
  // `null` = the finding has no transaction UUID (historical run never
  // backfilled) — same empty state as a transaction with no lines.
  if (!entry || entry.lines.length === 0) {
    return <p className="text-sm text-brand-slate">No lines on this entry.</p>;
  }

  // Drop columns where every cell is empty / null / undefined. NetSuite
  // exports carry 22 columns; on a typical 3-line bill ~half are blank
  // for every line. Hiding them keeps the table inside the viewport on
  // common 1080p / 1440p screens without horizontal scroll.
  const allColumns: string[] = [];
  const seen = new Set<string>();
  for (const line of entry.lines) {
    for (const key of Object.keys(line)) {
      if (!seen.has(key)) {
        seen.add(key);
        allColumns.push(key);
      }
    }
  }
  const nonEmpty = allColumns.filter((c) =>
    entry.lines.some((line) => !isEmptyJournalValue(line[c])),
  );
  const priority = _JOURNAL_PRIORITY_COLUMNS.filter((c) => nonEmpty.includes(c));
  const rest = nonEmpty.filter(
    (c) => !(priority as readonly string[]).includes(c),
  );
  const columns = [...priority, ...rest];

  return (
    // Negative horizontal margin breaks out of the CardContent's px-6
    // padding so the table viewport spans the full card width. The
    // table itself uses its natural (`w-max`) width — `w-full` would
    // force it to fit the wrapper and the scrollbar would never appear.
    // `min-w-0` upstream (on the parent section) keeps the wrapper
    // sized to the available column width; this wrapper's overflow-x
    // produces the scroll when the table is wider than that.
    <div className="-mx-6 overflow-x-auto">
      <table className="w-max border-separate border-spacing-0 text-xs">
        <thead className="bg-muted/40">
          <tr>
            {columns.map((c, i) => (
              <th
                key={c}
                className={cn(
                  'whitespace-nowrap border-b border-border px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-brand-slate',
                  i === 0 && 'pl-6',
                  i === columns.length - 1 && 'pr-6',
                  c === 'Amount' && 'text-right',
                )}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entry.lines.map((line, rowIdx) => (
            <tr
              key={rowIdx}
              className={cn(
                rowIdx % 2 === 1 && 'bg-muted/20',
                'hover:bg-muted/40',
              )}
            >
              {columns.map((c, i) => (
                <td
                  key={c}
                  className={cn(
                    'whitespace-nowrap border-b border-border/40 px-3 py-1.5 text-brand-navy',
                    i === 0 && 'pl-6',
                    i === columns.length - 1 && 'pr-6',
                    c === 'Amount'
                      ? 'text-right font-mono tabular-nums'
                      : 'font-mono tabular-nums',
                  )}
                >
                  {formatJournalCell(c, line[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isEmptyJournalValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function formatJournalCell(column: string, value: unknown): string {
  if (isEmptyJournalValue(value)) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (column === 'Amount' && typeof value === 'number') {
    return formatNumber(value);
  }
  if (column === 'Amount' && typeof value === 'string') {
    const n = Number(value);
    if (!Number.isNaN(n)) return formatNumber(n);
  }
  if (column === 'Date' && typeof value === 'string') {
    // ISO date / datetime → just the date portion. Source already gives
    // us `2024-05-15T00:00:00`-shape strings via the python ingest.
    return value.split('T')[0];
  }
  return String(value);
}

function formatNumber(n: number): string {
  // Match the rest of the card's monetary formatting (en-CA, two
  // decimals, sign-prefixed for negatives).
  return n.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
